import {
  Accordion,
  AccordionContent,
  AccordionTrigger,
  Code
} from '@deque/cauldron-react';
import classNames from 'classnames';
import React, { type ReactElement, useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import AnalyticsEnabledLink from '~/common/components/AnalyticsEnabledLink';
import PageTitle from '~/common/components/PageTitle';
import { ProductSlugs } from '~/common/constants';
import { useAnalyticsInstance } from '~/common/contexts/analytics';
import { useEnterprises } from '~/common/contexts/enterprises';
import styles from './AxeMCPServerGettingStarted.css';

// Built with `JSON.stringify` rather than hand-written template literals so the
// snippets we tell people to paste are guaranteed to be valid, consistently
// pretty-printed JSON — no hand-managed indentation or escaped inner quotes.
const stringifyConfig = (config: unknown): string =>
  JSON.stringify(config, null, 2);

const NPM_API_KEY_CONFIG = stringifyConfig({
  mcpServers: {
    axe: {
      command: 'npx',
      args: ['-y', 'axe-mcp-server'],
      env: { AXE_API_KEY: '<your-free-tier-api-key>' }
    }
  }
});

const DOCKER_API_KEY_CONFIG = stringifyConfig({
  mcpServers: {
    axe: {
      command: 'docker',
      args: [
        'run',
        '--add-host=host.docker.internal:host-gateway',
        '-i',
        '--rm',
        '-e',
        'AXE_SERVER_URL',
        '-e',
        'AXE_API_KEY',
        'dequesystems/axe-mcp-server:latest'
      ],
      env: { AXE_API_KEY: '<your-free-tier-api-key>' }
    }
  }
});

const DOCKER_OAUTH_CONFIG = stringifyConfig({
  mcpServers: {
    axe: {
      command: 'sh',
      args: [
        '-c',
        'docker run --add-host=host.docker.internal:host-gateway -i --rm -e "AXE_ACCESS_TOKEN=$(npx -y @deque/axe-auth token)" dequesystems/axe-mcp-server:latest'
      ]
    }
  }
});

const NPM_OAUTH_CONFIG = stringifyConfig({
  mcpServers: {
    axe: {
      command: 'sh',
      args: [
        '-c',
        'AXE_ACCESS_TOKEN=$(npx @deque/axe-auth token) npx -y axe-mcp-server'
      ]
    }
  }
});

const AXE_AUTH_NPM_URL = 'https://www.npmjs.com/package/@deque/axe-auth';
const SETUP_DOCS_URL =
  'https://docs.deque.com/devtools-server/4.0.0/en/axe-mcp-server';
const UPGRADE_URL =
  'https://www.deque.com/axe/devtools/web-accessibility/?utm_source=axe-mcp-signup';

const AxeMCPServerGettingStarted = (): ReactElement => {
  const { t } = useTranslation();
  const { isAdmin } = useEnterprises();
  // Mirrors Installed.tsx's view-tracking-on-mount pattern. `CrossProductAnalytics`
  // (the analytics class backing ProductSlugs.axeMCPServer) doesn't yet have a
  // dedicated "view getting started page" event, so this reuses the closest
  // existing href-based view-tracking call. No Amplitude key is currently wired
  // for this funnel (see analyticsInstances.tsx), so this is a no-op today; it's
  // added for parity/future-wiring consistency.
  const analytics = useAnalyticsInstance(ProductSlugs.axeMCPServer);

  useEffect(() => {
    analytics.viewSignUp({ href: window.location.href });
  }, []);

  return (
    <>
      <PageTitle title={t('Get started with the Axe MCP Server')} />
      <div className={classNames('narrowCenteredContainer', styles.wrap)}>
        <h1>{t('Get started with the Axe MCP Server')}</h1>
        <p>
          <Trans>
            Your free account is ready. The free tier gives you{' '}
            <strong>
              unlimited <code>analyze</code> scans
            </strong>
            . Follow the steps below to run your first scan.
          </Trans>
        </p>

        {isAdmin && (
          <p>
            <Trans>
              Give your colleagues access in the{' '}
              <Link to="/user-access">user management portal</Link>.
            </Trans>
          </p>
        )}

        <h2>{t('Step 1 — Connect the server')}</h2>
        <p>
          <Trans>
            The Axe MCP Server authenticates one of two ways, so pick whichever
            fits your setup. Provide <strong>either</strong> an API key{' '}
            <strong>or</strong> an OAuth token, never both.
          </Trans>
        </p>

        <h3>{t('Option A — API key')}</h3>
        <p>
          <Trans>
            Create a free-tier API key from the{' '}
            <Link to="/settings">API Keys</Link> page, then hand it to the
            server via the <code>AXE_API_KEY</code> environment variable (used
            in step 2).
          </Trans>
        </p>

        <h3>{t('Option B — OAuth')}</h3>
        <p>
          <Trans>
            Authenticate with the{' '}
            <AnalyticsEnabledLink href={AXE_AUTH_NPM_URL}>
              <code>@deque/axe-auth</code>
            </AnalyticsEnabledLink>{' '}
            CLI (<code>npx -y @deque/axe-auth login</code>). This stores
            short-lived credentials on your machine; supply the token via{' '}
            <code>AXE_ACCESS_TOKEN</code> (used in step 2).
          </Trans>
        </p>

        <h2>{t('Step 2 — Add it to your client')}</h2>
        <p>{t('Add the server to your MCP client config.')}</p>

        <Accordion>
          <AccordionTrigger heading={{ level: '3' }}>
            {t('API key — AXE_API_KEY')}
          </AccordionTrigger>
          <AccordionContent className={styles.accordionContent}>
            <h4>{t('npm')}</h4>
            <Code
              scrollable
              language="javascript"
              aria-label={t('npm MCP client configuration using an API key')}
              allowCopy
              copyButtonProps={{
                children: t(
                  'Copy npm MCP client configuration using an API key'
                )
              }}
            >
              {NPM_API_KEY_CONFIG}
            </Code>
            <h4>{t('Docker')}</h4>
            <Code
              scrollable
              language="javascript"
              aria-label={t('Docker MCP client configuration using an API key')}
              allowCopy
              copyButtonProps={{
                children: t(
                  'Copy Docker MCP client configuration using an API key'
                )
              }}
            >
              {DOCKER_API_KEY_CONFIG}
            </Code>
          </AccordionContent>
        </Accordion>

        <Accordion>
          <AccordionTrigger heading={{ level: '3' }}>
            {t('OAuth — AXE_ACCESS_TOKEN')}
          </AccordionTrigger>
          <AccordionContent className={styles.accordionContent}>
            <p>
              <Trans>
                Authenticate once (step 1), then wrap the launch command with{' '}
                <code>sh -c</code> so a <strong>fresh</strong> token is injected
                on every start — axe-auth tokens are short-lived, so don&apos;t
                paste a static one.
              </Trans>
            </p>
            <h4>{t('Docker')}</h4>
            <Code
              scrollable
              language="javascript"
              aria-label={t('Docker MCP client configuration using OAuth')}
              allowCopy
              copyButtonProps={{
                children: t('Copy Docker MCP client configuration using OAuth')
              }}
            >
              {DOCKER_OAUTH_CONFIG}
            </Code>
            <h4>{t('npm')}</h4>
            <Code
              scrollable
              language="javascript"
              aria-label={t('npm MCP client configuration using OAuth')}
              allowCopy
              copyButtonProps={{
                children: t('Copy npm MCP client configuration using OAuth')
              }}
            >
              {NPM_OAUTH_CONFIG}
            </Code>
          </AccordionContent>
        </Accordion>

        <p>
          <Trans>
            For per-client setup (Cursor, Claude, VS Code), see the{' '}
            <AnalyticsEnabledLink href={SETUP_DOCS_URL}>
              setup docs
            </AnalyticsEnabledLink>
            .
          </Trans>
        </p>

        <h2>{t('Step 3 — Run your first scan')}</h2>
        <p>
          <Trans>
            Ask your AI agent to scan a page, e.g.{' '}
            <em>
              &quot;Analyze https://example.com for accessibility issues.&quot;
            </em>{' '}
            The assistant calls the <code>analyze</code> tool and returns the
            violations it found.
          </Trans>
        </p>

        <p>
          <Trans>
            Need advanced testing capabilities, automated Intelligent Guided
            Tests, Org-wide settings, or AI-generated remediation?{' '}
            <AnalyticsEnabledLink
              href={UPGRADE_URL}
              sendClickEvent={() => analytics.clickSignUpBuyNowLink()}
            >
              Upgrade to Axe DevTools for Web
            </AnalyticsEnabledLink>
            .
          </Trans>
        </p>
      </div>
    </>
  );
};

export default AxeMCPServerGettingStarted;
